import { fmtAge } from '@chula/shared';
import type { FallbackTier } from '@chula/shared';
import { TIER_HINT, TIER_TAG, ageChipText } from '../lib/tierDisplay';

interface PanelHeaderProps {
  /** Section title — rendered as eyebrow */
  title: string;
  /** Data age in minutes from NormalizedFeed.meta.ageMinutes */
  ageMinutes?: number | null;
  /**
   * Provenance tier from NormalizedFeed.meta.fallbackTier — REQUIRED (audit
   * #9): an omitted tier used to default to `live`, so a panel that forgot it
   * silently claimed live data. Pass `null` only while the feed is still
   * loading and its tier is genuinely unknown (no badge, no age chip).
   */
  fallbackTier: FallbackTier | null;
  /** Short source identifier e.g. "open-meteo" | "bma-ckan" */
  source?: string;
  /** Extra content on the right side (e.g. a refresh button) */
  actions?: React.ReactNode;
}

/**
 * Age-chip tone. Only a LIVE feed can look "fresh" (the accent); anything
 * modeled, cached or saved stays grey whatever its age.
 */
function ageClass(minutes: number | null | undefined, tier: FallbackTier | null): string {
  if (tier !== 'live') return '';
  if (minutes == null) return '';
  if (minutes < 5) return 'data-age--fresh';
  if (minutes < 30) return '';
  if (minutes < 120) return 'data-age--warn';
  return 'data-age--stale';
}

/**
 * Returns 'panel-hdr--stale' when the feed is overdue enough that a
 * governor scanning the room should notice. Threshold: 2 h or unavailable.
 * Reference data has no freshness, so it is never "stale".
 */
function headerStalenessClass(minutes: number | null | undefined, tier: FallbackTier | null): string {
  if (tier === 'unavailable') return 'panel-hdr--stale';
  if (tier === 'reference') return '';
  if (minutes != null && minutes >= 120) return 'panel-hdr--stale';
  return '';
}

/**
 * PanelHeader — standard section header for all dashboard panels.
 *
 * Left: the panel title. Right: source tag, tier badge (any non-live tier,
 * always as TEXT — never colour alone), data-age chip, then `actions`.
 *
 * Honesty rule: the word LIVE is only ever printed for the `live` tier
 * (see ageChipText). Tier badges are keyboard-focusable so their plain-
 * language explanation is reachable without a mouse.
 */
export function PanelHeader({ title, ageMinutes, fallbackTier, source, actions }: PanelHeaderProps) {
  // Defensive: untyped JS callers / tests may still pass undefined.
  const tier: FallbackTier | null = fallbackTier ?? null;
  const tl = tier ? TIER_TAG[tier] : '';
  const chip = ageChipText(tier, ageMinutes);
  const stale = headerStalenessClass(ageMinutes, tier);

  return (
    <div className={`panel-hdr${stale ? ` ${stale}` : ''}`}>
      <span className="eyebrow mono">{title}</span>

      {source && (
        <span
          className="panel-source-tag mono"
          title={`Data source: ${source} — where this panel's numbers come from`}
        >
          {source.toUpperCase()}
        </span>
      )}

      {tier && tl && (
        <span
          className={`panel-tier-warn tier-tag mono${tier === 'unavailable' ? ' panel-tier-warn--crit' : ''}`}
          title={`Data quality: ${tier} — not live. ${TIER_HINT[tier]}.`}
          data-tip={`${tl}: ${TIER_HINT[tier]}`}
          tabIndex={0}
          aria-label={`${tl} — ${TIER_HINT[tier]}`}
        >
          {tl}
        </span>
      )}

      {tier && chip && (
        <span
          className={`data-age mono ${ageClass(ageMinutes, tier)}`}
          title={`Last updated: ${fmtAge(ageMinutes)} · tier: ${tier} — ${TIER_HINT[tier]}`}
        >
          {chip}
        </span>
      )}

      {actions}
    </div>
  );
}
